//ionic
import { IonText, IonList, IonRouterLink, useIonRouter } from "@ionic/react";
import { useLocation } from "react-router-dom";

//css
import "./sidebar.css";

//components
import { UserInfo } from "./UserInfo";

//recoil
import { useSetRecoilState, useRecoilValue } from "recoil";
import { examAbandonAtom } from "recoil/examAbandonAtom";
import { tokenState } from "recoil/tokenAtom";
import { Accordion } from "./Accordion";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
import { FacebookLogin } from "@capacitor-community/facebook-login";
import { initialStateUser, userInfoState } from "recoil/User";
import { Capacitor } from "@capacitor/core";

type MenuRefType = {
  open: () => Promise<boolean>;
  close: () => Promise<boolean>;
};

interface SidebarProps {
  menuRef: React.RefObject<MenuRefType>;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const closeMenu = (menuRef: React.RefObject<MenuRefType>) => async () => {
  await menuRef.current?.close();
};

export const Sidebar: React.FC<SidebarProps> = ({ menuRef, show, setShow }) => {
  const navigation = useIonRouter();
  const location = useLocation();
  const examAbandon = useRecoilValue(examAbandonAtom);
  const setExamAbandon = useSetRecoilState(examAbandonAtom);
  const setToken = useSetRecoilState(tokenState);
  const setInfoUser = useSetRecoilState(userInfoState);

  const isOnExamPage = location.pathname.startsWith("/examen/");

  const handleSidebarLinkClick = (
    e: React.MouseEvent,
    targetPath: string,
  ) => {
    if (isOnExamPage && examAbandon.inProgress) {
      e.preventDefault();
      e.stopPropagation();
      setExamAbandon((prev) => ({
        ...prev,
        requestedNavigation: targetPath,
      }));
      closeMenu(menuRef)();
    } else {
      closeMenu(menuRef)();
    }
  };

  const handleLogOut = async () => {
    try {
      // Ejecutar signOut SOLO en dispositivos nativos
      if (Capacitor.isNativePlatform()) {
        await GoogleAuth.signOut();
      }
    } catch (err) {
      console.warn("Google signOut error (ignorable)", err);
    }
    setToken({ jwtToken: null, userId: null });
    setInfoUser(initialStateUser);
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("user");
    localStorage.removeItem("pw");
    localStorage.removeItem("device");
    localStorage.removeItem("client");
    localStorage.removeItem("googleToken");
    GoogleAuth.signOut();
    navigation.push("/login/signin", "forward", "replace");
    closeMenu(menuRef);
  };

  return (
    <div className="sb-container">
      <div>
        <UserInfo
          menuRef={menuRef}
          examInProgress={isOnExamPage && examAbandon.inProgress}
          onExamNavigateRequest={(path: string) =>
            setExamAbandon((prev) => ({ ...prev, requestedNavigation: path }))
          }
        />
        <IonList className="sb-ion-list">
          <IonRouterLink
            routerLink="/tabs/home"
            style={{ marginTop: "12px" }}
            onClick={(e) => handleSidebarLinkClick(e, "/tabs/home")}
          >
            <IonText className="sb-ion-text" onClick={closeMenu(menuRef)}>
              Home
            </IonText>
          </IonRouterLink>

          <IonRouterLink
            routerLink="/tabs/aprendizaje"
            className="sb-mt-24"
            onClick={(e) => handleSidebarLinkClick(e, "/tabs/aprendizaje")}
          >
            <IonText className="sb-ion-text" onClick={closeMenu(menuRef)}>
              Programa de Aprendizaje
            </IonText>
          </IonRouterLink>

          <Accordion
            show={show}
            setShow={setShow}
            onClick={closeMenu(menuRef)}
            examInProgress={isOnExamPage && examAbandon.inProgress}
            onExamNavigateRequest={(path: string) =>
              setExamAbandon((prev) => ({ ...prev, requestedNavigation: path }))
            }
          />

          <IonRouterLink
            routerLink="/sesiones"
            className="sb-mt-24"
            onClick={(e) => handleSidebarLinkClick(e, "/sesiones")}
          >
            <IonText className="sb-ion-text" onClick={closeMenu(menuRef)}>
              Sesiones en Vivo
            </IonText>
          </IonRouterLink>

          <IonRouterLink
            routerLink="/campus"
            className="sb-mt-24"
            onClick={(e) => handleSidebarLinkClick(e, "/campus")}
          >
            <IonText className="sb-ion-text" onClick={closeMenu(menuRef)}>
              Próximos Campus
            </IonText>
          </IonRouterLink>

          <div className="sb-divider" style={{ margin: "12px 0" }}></div>

          <IonRouterLink
            routerLink="/perfil"
            onClick={(e) => handleSidebarLinkClick(e, "/perfil")}
          >
            <IonText className="sb-ion-text" onClick={closeMenu(menuRef)}>
              Perfil
            </IonText>
          </IonRouterLink>

          <IonRouterLink
            routerLink="/tabs/progress"
            className="sb-mt-24"
            onClick={(e) => handleSidebarLinkClick(e, "/tabs/progress")}
          >
            <IonText className="sb-ion-text" onClick={closeMenu(menuRef)}>
              Progreso
            </IonText>
          </IonRouterLink>

          <IonRouterLink
            routerLink="/tabs/help"
            className="sb-mt-24"
            onClick={(e) => handleSidebarLinkClick(e, "/tabs/help")}
          >
            <IonText className="sb-ion-text" onClick={closeMenu(menuRef)}>
              Ayuda
            </IonText>
          </IonRouterLink>
        </IonList>
      </div>

      <IonText className="sb-ion-text" onClick={handleLogOut}>
        Cerrar Sesión
      </IonText>
    </div>
  );
};
